import React from 'react';
import { computed, action, makeObservable, observable } from "mobx";
import { isUUID, UUID } from "../../State/EvidenceDB/DatabaseTypes";
import { GlobalContext } from "./GlobalContext";
import { RecordingPanelState } from "./RecordingPanelState";

export const PageNames = ['', 'settings', 'privacy', 'license', 'recordings'] as const;
const PageNameSet = new Set<unknown>(PageNames);
export type PageName = typeof PageNames[number];
export const isPageName = (x: unknown): x is PageName => PageNameSet.has(x);


export const isRunningOnWebsite = window.location.href.startsWith('http');
export const isRunningInExtension = !isRunningOnWebsite;

export type ValidPagePath = `/${PageName}${'/' | ''}` | `/recordings/${UUID}`;

export class WindowRoutingState {
	_url: string;
	_uploadInProgressPercentage: number | undefined;

	@computed get url() { return this._url; }

	@computed get Url() { try { return new URL(this._url); } catch { return undefined; } }

	@computed get path() { return this.Url?.pathname; }
	@computed get hash() : string | undefined { return this.Url?.hash; }

	@computed get runningOnWebsite() {
		return isRunningOnWebsite
	}
	@computed get runningInExtension() {
		return isRunningInExtension
	}

	@computed get pathOrHashArray() {
		if (this.runningInExtension) {
			const hashStart = this.url.indexOf('#');
			if (hashStart >= 0) {
				return this.url.slice(hashStart + 1).split("/").filter(Boolean);
			}
		}
		if (this.Url == null) return [];
		const {pathname, hash} = this.Url;
		if (!this.Url.protocol.endsWith('-extension') && pathname.length > 1) {
			return pathname.substring(1).split("/").filter(Boolean)
		} else if (hash.length > 1) {
			return hash.substring(1).split("/").filter(Boolean)
		}
		return [];
	}

//	@computed get pathArray() { return this.path?.substring(1)?.split("/").filter(Boolean); }

	@computed get pathTop() { return this.pathOrHashArray[0]; }
	@computed get typedPathTop() { return isPageName(this.pathTop) ? this.pathTop : undefined }

	@computed get page(): PageName 	{
		return isPageName(this.pathTop) ? this.pathTop :
			isRunningInExtension ? 'recordings' :
			'';		
	}

	@computed get uploadInProgressPercentage() {
		return this._uploadInProgressPercentage;
	}

	@computed get recordingUuid() {
		const recordingUuid = this.pathOrHashArray?.[1];
		if (this.typedPathTop === "recordings" && isUUID(recordingUuid)) {
			return recordingUuid;
		}
	}

	@computed get recordingPanelState() {
		return RecordingPanelState.forRecordingUuid(this.globalContext, this.recordingUuid);
	}


	setUploadInProgressPercentage = action ( (percentage: number | undefined) => {
		this._uploadInProgressPercentage = percentage;
	});

	protected setUrl = action((url: string) => {
		this._url = url;
	});

	protected setPath = (path: string) => {
		const url = `${window.location.origin}${path}`;
		this.setUrl(url);
		return url;
	}

	pushPath = (path: ValidPagePath) => {
		window.history.pushState(undefined, '', this.setPath(path));
	};

	replacePath = (path: ValidPagePath) => {
		window.history.replaceState(undefined, '', this.setPath(path));
	};

	constructor(
		protected readonly globalContext: GlobalContext,
	) {
		this._url = window.location.href;
		// const startUrl = new URL(window.location.href);
		// ProcessInfo.log(`Constructing WindowRoutingState`, this.recordingsTableWriter.recordingInProgress, this.url);
		makeObservable(this, {
			"_url": observable,
			"_uploadInProgressPercentage": observable
		});
		// if (startUrl.pathname.length <= 1 && this.recordingsTableWriter.recordingInProgress != null) {
		// 	this.pushPath(`/recordings/${this.recordingsTableWriter.recordingInProgress.recordingUuid}`);
		// }

		window.addEventListener('popstate', () => {
			this.setUrl(window.location.href);
		})
	}
}

export const WindowRoutingStateContext = React.createContext<WindowRoutingState | undefined>(undefined);